import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { subDays } from "date-fns/subDays";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { Card } from "@tellescope/design-system";
import { ChevronRight, ArrowUp, ArrowDown, Minus } from "lucide-react";
import type { EnduserObservation } from "@tellescope/types-models";
import { buildSeries, METRICS } from "./metrics";
import { WeekMacroBarChart } from "./WeekMacroBarChart";
import type { WeightDisplayUnit } from "../../../utils/nutritionUnitConversions";

interface WeightChangeBadgeProps {
  change: number;
  unit: WeightDisplayUnit;
}

/** Neutral change-since-last-weigh-in badge — direction shown, no good/bad coloring (weight tracking is goal-free). */
const WeightChangeBadge = ({ change, unit }: WeightChangeBadgeProps) => {
  const { t } = useTranslation("portal");
  const rounded = Number(change.toFixed(1));
  const Icon = rounded > 0 ? ArrowUp : rounded < 0 ? ArrowDown : Minus;
  const label = rounded === 0 ? t("No change") : `${Math.abs(rounded).toFixed(1)} ${unit}`;

  return (
    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-muted text-muted-foreground text-xs font-medium tabular-nums">
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
};

interface WeightHeroCardProps {
  weightEntries: EnduserObservation[];
  foodEntries: EnduserObservation[];
  weightUnit: WeightDisplayUnit;
  onClick: () => void;
}

export const WeightHeroCard = ({ weightEntries, foodEntries, weightUnit, onClick }: WeightHeroCardProps) => {
  const { t } = useTranslation("portal");

  const series30d = useMemo(() => {
    const cutoff = subDays(new Date(), 30);
    return buildSeries(METRICS.weight, weightEntries, [], [], weightUnit).filter((p) => new Date(p.date) >= cutoff);
  }, [weightEntries, weightUnit]);

  const latest = series30d.at(-1);
  const prev = series30d.length >= 2 ? series30d[series30d.length - 2] : undefined;

  return (
    <Card
      variant="floating"
      padding="root"
      className="cursor-pointer hover:bg-muted/40 transition-colors"
      onClick={onClick}
    >
      <Card.Body className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("Weight")}</p>
            {latest && (
              <p className="text-2xl font-bold tabular-nums">
                {latest.value} <span className="text-sm font-normal text-muted-foreground">{weightUnit}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {latest && prev && <WeightChangeBadge change={latest.value - prev.value} unit={weightUnit} />}
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
        {series30d.length >= 2 ? (
          <div className="h-12">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series30d} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="var(--color-accent, #171717)"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground py-2">
            {t("Gathering information — check back after a few more entries.")}
          </p>
        )}
        <WeekMacroBarChart foodEntries={foodEntries} />
      </Card.Body>
    </Card>
  );
};
